/**
 * Frontend IHI (Individual Healthcare Identifier) validation — immediate
 * practitioner UX feedback and local save gating.
 *
 * B2.1A: aligned with the strict UC.010 manual-capture contract
 * (HI_010_005801): a manually entered IHI must contain all sixteen digits as
 * one continuous digit string and must satisfy the Luhn check digit algorithm
 * (HI Service Conformance Profile, Appendix D). An IHI that fails either rule
 * must not be stored, and the operator is alerted.
 *
 * No normalisation is performed: spaces, tabs, line breaks, letters,
 * punctuation, signs, decimal points and exponent markers are rejected rather
 * than stripped, so a prohibited representation can never be silently
 * "repaired" into a different value that then validates.
 */
export const validateIHI = (ihi: string): { isValid: boolean; error?: string } => {
	// Reject any non-digit content (whitespace, letters, punctuation, signs,
	// decimal points, exponent markers) instead of stripping it.
	if (typeof ihi !== 'string' || !/^[0-9]*$/.test(ihi)) {
		return { isValid: false, error: 'IHI must contain digits only, with no spaces or other characters.' };
	}

	// Exactly sixteen continuous digits (covers blank, too-short and too-long).
	if (ihi.length !== 16) {
		return { isValid: false, error: 'IHI must be exactly 16 digits.' };
	}

	// Luhn "double-add-double" check over the complete 16-digit representation:
	// double alternate digits starting from the digit left of the check digit,
	// add the individual digits of each product to the unaffected digits, and
	// the total including the check digit must be divisible by 10.
	let sum = 0;
	for (let i = 0; i < 16; i++) {
		let digit = ihi.charCodeAt(15 - i) - 48;
		if (i % 2 === 1) {
			digit *= 2;
			if (digit > 9) digit -= 9;
		}
		sum += digit;
	}

	if (sum % 10 !== 0) {
		return { isValid: false, error: 'IHI check digit is invalid.' };
	}

	return { isValid: true };
};
